import { z } from "zod";

import { isoDateTimeSchema } from "./common";

/**
 * `POST /api/settings/picker-token` 的请求体。
 * 为一条已保存的元素可见规则签发店面拾取令牌；Tenant 来自商家鉴权，不能由请求指定。
 */
export const pickerTokenRequestSchema = z.strictObject({
  ruleId: z.string().trim().min(1).max(128),
});

export type PickerTokenRequest = z.infer<typeof pickerTokenRequestSchema>;

/**
 * 签发响应：后台新标签打开 `url`（店面首页，拾取令牌在查询参数里，挂件读入后立即摘除）。
 * `expiresAt` 是令牌失效时间，进入或换页只校验不消费，成功保存才消费。
 */
export const pickerTokenResponseSchema = z.object({
  url: z.url(),
  expiresAt: isoDateTimeSchema,
});

export type PickerTokenResponse = z.infer<typeof pickerTokenResponseSchema>;

/**
 * `POST /apps/gonovus-chat/picker/inspect` 的请求体：进入拾取或同标签换页时
 * 只读校验拾取令牌，不消费。
 *
 * 令牌只能走请求体：查询参数会被 App Proxy 鉴权在找不到 offline Session 时
 * 整串写进日志，URL 携带令牌等于记录明文；自定义请求头也不可用——Shopify
 * 代理层只保证转发请求方法与请求体，其余请求头会被剥离。
 */
export const pickerInspectRequestSchema = z.strictObject({
  token: z.string().min(1).max(128),
});

export type PickerInspectRequest = z.infer<typeof pickerInspectRequestSchema>;

/**
 * `POST /apps/gonovus-chat/picker/inspect` 的只读校验响应：令牌绑定规则的 id、
 * 提示文案与已保存选择器。
 */
export const pickerInspectResponseSchema = z.object({
  ruleId: z.string(),
  prompt: z.string(),
  selector: z.string(),
});

export type PickerInspectResponse = z.infer<typeof pickerInspectResponseSchema>;

/**
 * `POST /apps/gonovus-chat/picker` 保存目标元素的请求体；保存成功时原子消费令牌，
 * 重复或并发提交至多一个写入成功。
 */
export const pickerSaveRequestSchema = z.strictObject({
  token: z.string().min(1).max(128),
  selector: z.string().min(1).max(1024),
});

export type PickerSaveRequest = z.infer<typeof pickerSaveRequestSchema>;

/** `POST /apps/gonovus-chat/picker` 的保存响应：写入结果的规则 id 与选择器。 */
export const pickerSaveResponseSchema = z.object({
  ruleId: z.string(),
  selector: z.string(),
});

export type PickerSaveResponse = z.infer<typeof pickerSaveResponseSchema>;
